/**
 * Gemini AI Service
 * Handles interactions with Google's Gemini 1.5 Flash API for video analysis
 */

// Get API key from environment variables
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_API_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent";

/**
 * Analyzes a video using Gemini 1.5 Flash API
 * @param {File} videoFile - The video file to analyze
 * @param {Object} protocol - The protocol criteria to evaluate against
 * @param {String} scenarioText - The mock drill scenario text
 * @returns {Promise<Object>} - Analysis results
 */
export const analyzeVideoWithGemini = async (videoFile, protocol, scenarioText) => {
  try {
    // Convert video to base64
    const base64Video = await fileToBase64(videoFile);
    
    // Use the actual mock drill scenario that's passed from the FallPreventionCourse component
    // Example: "A 72-year-old woman with arthritis and limited mobility has fallen while attempting to get out of bed. The patient is conscious but disoriented. Demonstrate the proper emergency response protocol, including assessment and communication procedures."
    const mockDrillScenario = scenarioText || "A patient has fallen and requires assistance. Demonstrate the proper emergency response protocol.";

    // Extract key information from the scenario to customize the protocol
    const extractScenarioInfo = (scenario) => {
      // Default values if extraction fails
      let info = {
        age: '70+',
        gender: 'patient',
        condition: 'unspecified medical condition',
        location: 'unspecified location',
        status: 'requiring assistance',
        mobilityIssue: false,
        cognitiveIssue: false,
        painIssue: false,
        specificConditions: []
      };
      
      try {
        // Extract age
        const ageMatch = scenario.match(/(\d+)-year-old/);
        if (ageMatch) info.age = ageMatch[1];
        
        // Extract gender
        if (scenario.includes('woman') || scenario.includes('female')) info.gender = 'woman';
        if (scenario.includes('man') || scenario.includes('male')) info.gender = 'man';
        
        // Extract medical condition
        const conditionMatch = scenario.match(/with ([^\.]+ (disease|impairment|arthritis|mobility|neuropathy|dementia|COPD|diabetes|stroke|osteoporosis))/);
        if (conditionMatch) info.condition = conditionMatch[1];
        
        // Check for specific conditions
        const conditions = [
          { term: 'Parkinson', flag: 'parkinsons', description: 'Parkinson\'s disease' },
          { term: 'arthritis', flag: 'arthritis', description: 'arthritis' },
          { term: 'dementia', flag: 'dementia', description: 'dementia' },
          { term: 'cognitive', flag: 'cognitive', description: 'cognitive impairment' },
          { term: 'diabetes', flag: 'diabetes', description: 'diabetes' },
          { term: 'neuropathy', flag: 'neuropathy', description: 'neuropathy' },
          { term: 'stroke', flag: 'stroke', description: 'stroke history' },
          { term: 'osteoporosis', flag: 'osteoporosis', description: 'osteoporosis' },
          { term: 'mobility', flag: 'mobility', description: 'mobility issues' },
          { term: 'COPD', flag: 'copd', description: 'COPD' },
          { term: 'vision', flag: 'vision', description: 'vision impairment' }
        ];
        
        conditions.forEach(c => {
          if (scenario.toLowerCase().includes(c.term.toLowerCase())) {
            info.specificConditions.push(c.description);
          }
        });
        
        // Extract patient status
        if (scenario.includes('unable to move') || scenario.includes('limited mobility')) {
          info.mobilityIssue = true;
        }
        
        if (scenario.includes('confused') || scenario.includes('disoriented') || 
            scenario.includes('cognitive') || scenario.includes('dementia')) {
          info.cognitiveIssue = true;
        }
        
        if (scenario.includes('pain') || scenario.includes('bruising') || 
            scenario.includes('injury')) {
          info.painIssue = true;
        }
      } catch (e) {
        console.error('Error parsing scenario:', e);
      }
      
      return info;
    };
    
    // Get scenario information
    const scenarioInfo = extractScenarioInfo(mockDrillScenario);
    console.log('Extracted scenario info:', scenarioInfo);
    
    // Build the protocol steps based on the scenario information
    let protocolSteps = [];
    
    // Step 1: Immediate Response & Safety Check
    let step1 = [
      "Ensure the environment is safe and free from further hazards.",
      "Assess if the patient is conscious and responsive.",
      "Check for any immediate signs of severe injury, such as head trauma or fractures."
    ];
    
    if (scenarioInfo.specificConditions.includes('Parkinson\'s disease')) {
      step1.push("For a patient with Parkinson's disease: Check for tremors, rigidity, or freezing that may have contributed to the fall.");
      step1.push("Be aware that Parkinson's medications may cause orthostatic hypotension, which could have contributed to the fall.");
    }
    
    if (scenarioInfo.specificConditions.includes('dementia') || scenarioInfo.specificConditions.includes('cognitive impairment')) {
      step1.push("For a patient with cognitive impairment: Use simple, clear communication and allow extra time for responses.");
      step1.push("Be aware that the patient may not accurately recall how the fall occurred.");
    }
    
    if (scenarioInfo.specificConditions.includes('diabetes')) {
      step1.push("For a patient with diabetes: Check for signs of hypoglycemia which may have contributed to the fall.");
      step1.push("Be aware that diabetic neuropathy may affect sensation in extremities.");
    }
    
    if (scenarioInfo.specificConditions.includes('stroke history')) {
      step1.push("For a patient with stroke history: Check for new neurological symptoms that may indicate a new stroke event.");
      step1.push("Be aware of potential weakness or paralysis on one side that may have contributed to the fall.");
    }
    
    if (scenarioInfo.mobilityIssue) {
      step1.push("Since the patient is unable to move without assistance, do not attempt to move them until a thorough assessment is complete.");
      step1.push("Reassure the patient that help is available and explain all actions before performing them.");
    }
    
    if (scenarioInfo.cognitiveIssue) {
      step1.push("Since the patient is confused/disoriented, use simple, clear communication and frequent reassurance.");
      step1.push("Avoid overwhelming the patient with too many questions or instructions at once.");
    }
    
    // Step 2: Physical Assessment & Injury Evaluation
    let step2 = [
      "Evaluate for pain, swelling, or deformities, especially in the wrist.",
      "Conduct a basic neurological check for confusion or cognitive impairment.",
      "Determine if the patient can move or bear weight safely."
    ];
    
    if (scenarioInfo.specificConditions.includes('Parkinson\'s disease')) {
      step2.push("For a patient with Parkinson's disease: Assess for any changes in rigidity or tremor compared to baseline.");
      step2.push("Check medication schedule as missed doses may worsen symptoms and affect mobility.");
    }
    
    if (scenarioInfo.specificConditions.includes('arthritis')) {
      step2.push("For a patient with arthritis: Pay special attention to arthritic joints which may have been injured in the fall.");
      step2.push("Be aware that arthritis may limit the patient's range of motion during assessment.");
    }
    
    if (scenarioInfo.specificConditions.includes('osteoporosis')) {
      step2.push("For a patient with osteoporosis: Be especially vigilant for fractures, which can occur with minimal trauma.");
      step2.push("Check for height loss or spinal deformity which may indicate vertebral fractures.");
    }
    
    if (scenarioInfo.painIssue) {
      step2.push("Since the patient is experiencing pain, document the location, severity, and characteristics of the pain.");
      step2.push("Use a pain scale appropriate for the patient's cognitive status to assess severity.");
    }
    
    if (scenarioInfo.mobilityIssue) {
      step2.push("Since the patient is unable to move without assistance, perform a thorough assessment before attempting any movement.");
      step2.push("Test range of motion passively, being attentive to any expressions of pain.");
    }
    
    // Step 3: Decision on Movement & Assistance
    let step3 = [
      "If no major injury, assist the patient into a seated position before standing.",
      "If a fracture or serious injury is suspected, keep the patient still and stabilize the affected limb.",
      "Apply ice to reduce swelling and arrange for medical transport if needed."
    ];
    
    if (scenarioInfo.specificConditions.includes('Parkinson\'s disease')) {
      step3.push("For a patient with Parkinson's disease: Allow extra time for movement due to bradykinesia (slowness of movement).");
      step3.push("Be aware of potential 'freezing' episodes and provide verbal cues to help overcome them.");
    }
    
    if (scenarioInfo.specificConditions.includes('arthritis')) {
      step3.push("For a patient with arthritis: Use proper body mechanics to avoid putting pressure on arthritic joints.");
      step3.push("Move slowly and gently to minimize pain in affected joints.");
    }
    
    if (scenarioInfo.specificConditions.includes('osteoporosis')) {
      step3.push("For a patient with osteoporosis: Use extreme caution when moving to prevent fractures.");
      step3.push("Support the spine in neutral alignment during any movement.");
    }
    
    if (scenarioInfo.mobilityIssue) {
      step3.push("Since the patient is unable to move without assistance, use proper lifting techniques and consider calling for additional help.");
      step3.push("Consider using assistive devices like a gait belt or mechanical lift if available and appropriate.");
    }
    
    // Step 4: Communication & Documentation
    let step4 = [
      "Notify emergency services or medical personnel with a clear report of the incident.",
      "Inform family or caregivers about the fall and next steps.",
      "Document the time, cause, injuries, and environmental factors for future fall prevention."
    ];
    
    if (scenarioInfo.specificConditions.includes('Parkinson\'s disease')) {
      step4.push("For a patient with Parkinson's disease: Document the timing of the fall in relation to medication schedule.");
      step4.push("Note any changes in Parkinson's symptoms before, during, or after the fall.");
    }
    
    if (scenarioInfo.specificConditions.includes('dementia') || scenarioInfo.specificConditions.includes('cognitive impairment')) {
      step4.push("For a patient with cognitive impairment: Ensure family members or regular caregivers are informed as the patient may not recall details.");
      step4.push("Document the patient's level of awareness and understanding of the event.");
    }
    
    if (scenarioInfo.cognitiveIssue) {
      step4.push("Since the patient is confused/disoriented, communicate with simple, clear statements and validate their feelings.");
      step4.push("Document baseline cognitive status and any changes following the fall.");
    }
    
    // Step 5: Post-Fall Monitoring & Prevention
    let step5 = [
      "Ensure medical evaluation for potential fractures or delayed complications.",
      "Assess and adjust assistive devices, medication, or home safety measures.",
      "Implement strength and balance training to prevent future falls."
    ];
    
    if (scenarioInfo.specificConditions.includes('Parkinson\'s disease')) {
      step5.push("For a patient with Parkinson's disease: Consider a medication review as timing and dosage may need adjustment.");
      step5.push("Recommend physical therapy focused on Parkinson's-specific balance and gait training.");
    }
    
    if (scenarioInfo.specificConditions.includes('diabetes')) {
      step5.push("For a patient with diabetes: Ensure regular blood glucose monitoring and proper medication management.");
      step5.push("Recommend proper footwear and regular foot checks to prevent complications.");
    }
    
    if (scenarioInfo.specificConditions.includes('vision impairment')) {
      step5.push("For a patient with vision impairment: Ensure adequate lighting and minimize glare in the living environment.");
      step5.push("Recommend contrasting colors for steps, thresholds, and other potential hazards.");
    }
    
    if (scenarioInfo.mobilityIssue) {
      step5.push("Since the patient has mobility issues, evaluate the need for additional assistive devices or home modifications.");
      step5.push("Consider a referral for physical therapy to improve strength and mobility.");
    }
    
    // Format the protocol steps
    const formatSteps = (steps, indent = '   ') => {
      return steps.map(step => `${indent}- ${step}`).join('\n');
    };
    
    // Combine all steps into the protocol text
    const protocolStepsText = `
1. Immediate Response & Safety Check
${formatSteps(step1)}

2. Physical Assessment & Injury Evaluation
${formatSteps(step2)}

3. Decision on Movement & Assistance
${formatSteps(step3)}

4. Communication & Documentation
${formatSteps(step4)}

5. Post-Fall Monitoring & Prevention
${formatSteps(step5)}
`;
    
    // Create the request payload
    const payload = {
      contents: [
        {
          parts: [
            {
              text: `You are a professional caretaker trainer with extensive experience in fall prevention and response protocols. You specialize in assessing caregiver performance through video analysis.
              
              SCENARIO CONTEXT:
              ${mockDrillScenario}
              
              IMPORTANT: Be lenient and supportive in your evaluation. Focus on what the caregiver did well and provide constructive feedback. Avoid being overly critical or expecting perfection. Remember that caregivers often work in challenging conditions with limited resources.
              
              Evaluate the caregiver's actions against these fall prevention protocol steps, paying special attention to patient-specific considerations:
              ${protocolStepsText}
              
              Additional protocol considerations to evaluate:
              - Proper body mechanics when assisting the patient
              - Appropriate use of any assistive devices
              - Clear, reassuring communication with the patient (adjusted for cognitive status if applicable)
              - Emotional support and reassurance provided to the patient
              - Speed and efficiency of response
              - Adaptations made for the specific patient's conditions (arthritis, diabetes, etc.)
              - Proper assessment of fall risk factors in the environment
              - Appropriate documentation and reporting procedures
              
              For each protocol step, determine if it was followed correctly, partially followed, or missed.
              Provide specific observations for each evaluation point. Be generous in your assessment - if the caregiver made a reasonable attempt at following a protocol step, consider it at least partially followed.
              
              YOU MUST PROVIDE EXACTLY THESE THREE OUTPUTS IN YOUR RESPONSE, in this exact order:
              
              1. SCORE: An overall score (0-100) based on the caregiver's performance. You MUST calculate the score as follows:
                 - Start with a base score of 30
                 - For each protocol step that was fully followed correctly, add 7 points
                 - For each protocol step that was partially followed, add 3 points
                 - For each protocol step that was missed or done incorrectly, add 0 points
                 - Add up to 10 additional points for exceptional performance in any area
                 
                 You MUST provide the final calculated score as a number between 30-100 (adjust base if needed to ensure score isn't below 30 even if many steps missed) and show your calculation. Format your score output exactly like this: "SCORE: [number] - [brief justification]"

              2. FEEDBACK: Provide a comprehensive, detailed paragraph of feedback based on your observations from the video. This feedback must:
                 - Be 1-2 paragraphs long and highly specific to this video
                 - Begin with an overall assessment of the caregiver's performance
                 - Highlight 1-2 specific strengths you observed, with concrete examples from the video
                 - Identify 1-2 specific areas for improvement, with detailed examples of what you observed
                 - Include specific recommendations that directly address the patient's underlying medical conditions (arthritis, diabetes, dementia, etc.)
                 - Reference specific moments or actions from the video ("At 0:45, you correctly...", "When helping the patient stand, I noticed...")
                 - Explain how the caregiver's actions impacted patient safety and comfort
                 - End with an encouraging statement that acknowledges the caregiver's efforts
                 
                 IMPORTANT: Your feedback must be highly personalized to what you actually observed in this specific video. DO NOT provide generic feedback. Every observation and recommendation must directly relate to something specific you saw in the video and must consider the patient's unique medical conditions.
                 
                 Format this section with the heading "FEEDBACK:" followed by your detailed assessment.

              3. ANALYSIS TABLE: A detailed analysis table with AT LEAST 7-8 safety aspects, with columns for:
                 - Safety Aspect
                 - Status (✅ for correct, ⚠️ for partial, ❌ for incorrect)
                 - Observations (focus on what was done well first, then note areas for improvement)
                 - Recommendations (phrase these as supportive suggestions that are specific to the patient's conditions)
                  
                  Your table MUST include these safety aspects (and you may add others):
                  1. Initial safety assessment
                  2. Patient assessment and vital signs check
                  3. Communication technique and clarity
                  4. Safe patient handling/transfer technique
                  5. Adaptation to patient's specific medical conditions
                  6. Use of appropriate assistive devices
                  7. Environmental hazard identification and mitigation
                  8. Documentation and reporting
                  
                  For EACH safety aspect, your observations and recommendations MUST specifically reference the patient's underlying conditions when relevant. For example, if the patient has arthritis, explain how this affects safe handling techniques and what specific adaptations should be made.
              
              YOUR RESPONSE MUST INCLUDE ALL THREE OF THESE ELEMENTS (SCORE, FEEDBACK, AND ANALYSIS TABLE). Do not skip any of them.
               REMEMBER: PROVIDE DETAILED FEEDBACK AND ANALYSIS BASED ON YOUR OBSERVATIONS.              `
            },
            {
              inlineData: {
                mimeType: videoFile.type,
                data: base64Video,
              },
            },
          ],
        },
      ],
    };

    // --- START TEMPORARY LOGGING ---
    console.log("--- Sending Payload to Gemini API ---");
    // Log only the text part to avoid huge base64 logs in console by default
    // You can uncomment the full payload log if needed, but be careful
    console.log(JSON.stringify({ contents: [{ parts: [{ text: payload.contents[0].parts[0].text }] }] }, null, 2)); 
    // console.log("Full Payload (includes base64 video):", JSON.stringify(payload, null, 2));
    console.log("--- End Payload Log ---");
    // --- END TEMPORARY LOGGING ---

    // Make the API call
    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    // --- START TEMPORARY LOGGING ---
    const rawResponseText = await response.text(); // Get raw text first
    console.log("--- Raw Response from Gemini API ---");
    console.log(rawResponseText);
    console.log("--- End Raw Response Log ---");
    // --- END TEMPORARY LOGGING ---

    // Now parse the raw text
    let responseData;
    try {
      responseData = JSON.parse(rawResponseText);
    } catch (e) {
      // Check if it's the specific error string
      if (rawResponseText.startsWith("NOT_FALL_RESPONSE_VIDEO:")) {
         console.log("Detected NOT_FALL_RESPONSE_VIDEO string");
         // Return the structured error object expected by the frontend
         return {
            isNotFallResponseVideo: true,
            score: 0,
            feedback: rawResponseText.replace("NOT_FALL_RESPONSE_VIDEO: ", "").trim(),
            results: []
         };
      } 
      console.error("Error parsing Gemini JSON response:", e);
      console.error("Raw response was:", rawResponseText);
      throw new Error(`Failed to parse API response. Status: ${response.status}`);
    }
    
    // Check for API errors after parsing
    if (!response.ok || responseData.error) {
      console.error('Gemini API Error:', responseData.error || responseData);
      throw new Error(`API Error: ${responseData.error?.message || response.statusText}`);
    }

    // Extract content and parse
    const content = responseData.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!content) {
      console.error('Invalid response structure:', responseData);
      throw new Error('Could not extract valid content from API response.');
    }

    // Handle potential NOT_FALL_RESPONSE_VIDEO text response from the API
    if (content.startsWith("NOT_FALL_RESPONSE_VIDEO:")) {
      return {
        isNotFallResponseVideo: true,
        score: 0,
        feedback: content.replace("NOT_FALL_RESPONSE_VIDEO: ", "").trim(),
        results: []
      };
    }
    
    // Parse the actual analysis content
    return parseGeminiResponse(content);

  } catch (error) {
    console.error('Error analyzing video:', error);
    throw error; // Re-throw the error for the component to catch
  }
};

/**
 * Converts a file to base64 encoding
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:video/mp4;base64,")
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Parses the Gemini API response text into a structured format
 * @param {string} responseText - The raw text response from Gemini
 * @returns {Object} - Structured analysis results
 */
const parseGeminiResponse = (responseText) => {
  try {
    console.log("Parsing Gemini Response Text:", responseText);

    // Check for the explicit error message
    if (responseText.includes("NOT_FALL_RESPONSE_VIDEO")) {
      return {
        isNotFallResponseVideo: true,
        score: 0,
          feedback: responseText.replace("NOT_FALL_RESPONSE_VIDEO: ", "").trim(),
        results: []
      };
    }
    
    // Find SCORE section
    const scoreMatch = responseText.match(/SCORE:\s*(\d+)\s*-\s*(.*)/);
    const score = scoreMatch ? parseInt(scoreMatch[1], 10) : 0;
    const scoreJustification = scoreMatch ? scoreMatch[2].trim().replace(/\*\*/g, '') : 'Score justification not found.';
    
    // Find FEEDBACK section
    const feedbackMatch = responseText.match(/FEEDBACK:\s*([\s\S]*?)(?=ANALYSIS TABLE:|$)/);
    const feedback = feedbackMatch ? feedbackMatch[1].trim().replace(/\*\*/g, '') : 'Feedback not found.';

    // Find ANALYSIS TABLE section
    const tableMatch = responseText.match(/ANALYSIS TABLE:\s*([\s\S]*)/);
    let analysisTable = [];
    let positivePoints = [];
    let importantSteps = [];
    let suggestions = [];

    if (tableMatch) {
      const tableText = tableMatch[1].trim();
      const lines = tableText.split('\n').filter(line => line.trim() !== '' && !line.startsWith('-') && !line.startsWith('='));
      
      if (lines.length > 1) {
        analysisTable = lines.slice(1).map(line => {
          const parts = line.split(/\s{2,}|\t|\||\-/).map(p => p.trim()).filter(p => p);
          const entry = {
            safetyAspect: parts[0] || 'N/A',
            status: parts[1] || 'N/A',
            observations: parts[2] || 'N/A',
            recommendations: parts[3] || 'N/A'
          };

          // Categorize entries based on status
          const category = entry.safetyAspect.split(':')[0];
          const detail = entry.observations;

          if (entry.status === '✅') {
            positivePoints.push({ category, detail });
          } else if (entry.status === '❌') {
            importantSteps.push({ category, detail: entry.recommendations });
          } else if (entry.status === '⚠️') {
            suggestions.push({ category, detail: entry.recommendations });
          }

          return entry;
        });
      }
    }

    // If no positive points were found in the analysis table, extract them from feedback
    if (positivePoints.length === 0) {
      const strengthsMatch = feedback.match(/What You Did Well:(.*?)(?=Important Steps to Include:|$)/s);
      if (strengthsMatch) {
        const strengths = strengthsMatch[1].trim().split('\n').filter(s => s.trim());
        positivePoints = strengths.map(s => {
          const [category, ...detailParts] = s.split(':');
          return {
            category: category.trim().replace(/^[•\-]\s*/, ''),
            detail: detailParts.join(':').trim()
          };
        });
      }
    }

    // If no important steps were found in the analysis table, extract them from feedback
    if (importantSteps.length === 0) {
      const stepsMatch = feedback.match(/Important Steps to Include:(.*?)(?=Suggestions for Enhancement:|$)/s);
      if (stepsMatch) {
        const steps = stepsMatch[1].trim().split('\n').filter(s => s.trim());
        importantSteps = steps.map(s => {
          const [category, ...detailParts] = s.split(':');
      return {
            category: category.trim().replace(/^[•\-]\s*/, ''),
            detail: detailParts.join(':').trim()
          };
        });
      }
    }

    // If no suggestions were found in the analysis table, extract them from feedback
    if (suggestions.length === 0) {
      const suggestionsMatch = feedback.match(/Suggestions for Enhancement:(.*?)(?=\n\n|$)/s);
      if (suggestionsMatch) {
        const suggs = suggestionsMatch[1].trim().split('\n').filter(s => s.trim());
        suggestions = suggs.map(s => {
          const [category, ...detailParts] = s.split(':');
          return {
            category: category.trim().replace(/^[•\-]\s*/, ''),
            detail: detailParts.join(':').trim()
          };
        });
      }
    }

    console.log("Parsed Data:", { score, scoreJustification, feedback, analysisTable, positivePoints, importantSteps, suggestions });

    // Return structured data
    return {
      isNotFallResponseVideo: false,
      score: score,
      scoreJustification: scoreJustification,
      feedback: feedback,
      results: analysisTable,
      positivePoints,
      importantSteps,
      suggestions
    };

  } catch (error) {
    console.error("Error parsing Gemini response content:", error);
    return {
      isNotFallResponseVideo: false,
      score: 0,
      feedback: `Error processing analysis results: ${error.message}. Raw response:\n${responseText}`,
      results: [],
      positivePoints: [],
      importantSteps: [],
      suggestions: [],
      parsingError: true
    };
  }
};

/**
 * Mock function for development/testing without API key
 * @param {File} videoFile - The video file to analyze
 * @param {Object} protocol - The protocol criteria to evaluate against (not used in mock)
 * @param {String} scenarioText - The mock drill scenario text (not used in mock)
 * @returns {Promise<Object>} - Mock analysis results
 */
export const mockAnalyzeVideo = async (videoFile, protocol, scenarioText) => {
  console.log('Starting mockAnalyzeVideo function with scenario:', scenarioText ? scenarioText.substring(0, 50) + '...' : 'none provided');
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // For testing purposes, we'll use the filename to determine if this is a non-fall video
  // This allows for more consistent testing - any filename containing 'nonfall' will be treated as not relevant
  // Also keeping the random chance (1 in 10) for variety in testing
  const filename = videoFile.name.toLowerCase();
  const isNotFallResponseVideo = filename.includes('nonfall') || filename.includes('non-fall') || filename.includes('non_fall') || Math.random() < 0.1;
  
  if (isNotFallResponseVideo) {
    console.log('Mock: Simulating a non-fall response video');
    return {
      isNotFallResponseVideo: true,
      score: 0,
      feedback: "This is not a video relevant for fall",
      results: []
    };
  }
  
  // IMPORTANT: This is a fixed mock response that should not be changed
  // to ensure consistent evaluation criteria between runs
  
  // Generate a more dynamic mock score between 60 and 90
  const mockScore = Math.floor(Math.random() * 31) + 60;
  console.log('Generated mock score:', mockScore);
  
  // Create a detailed feedback string
  const mockFeedback = "Your overall performance in handling this fall situation was good. You demonstrated proper technique in the initial assessment and patient assistance. The score of " + mockScore + " reflects your competence in following most of the fall protocol steps correctly. You showed particular strength in your immediate response and decision-making about patient movement. There are some areas where minor improvements could enhance patient safety and your own ergonomics. Continue practicing these skills while incorporating the specific recommendations provided.\n\nAt 0:32, I noticed you correctly assessed the patient's level of consciousness and checked for immediate injuries, which was excellent. Your communication throughout the process was clear and reassuring, which helped keep the patient calm. However, at 1:05, when helping the patient stand, I observed that you could improve your body mechanics by bending your knees more and keeping your back straight. For a patient with arthritis like this one, providing more support under the elbow joint would reduce strain on their inflamed joints. Overall, you demonstrated good awareness of fall protocol steps and patient safety considerations.";
  
  // Log the mock feedback to verify it's being set
  console.log('Generated mock feedback:', mockFeedback.substring(0, 100) + '...');
  
  // Ensure feedback is never empty or undefined
  if (!mockFeedback || mockFeedback.trim() === '') {
    console.warn('Mock feedback is empty, using default feedback');
    mockFeedback = `Your overall performance in handling this fall situation was good. You demonstrated proper technique in the initial assessment and patient assistance. The score of ${mockScore} reflects your competence in following most of the fall protocol steps correctly. You showed particular strength in your immediate response and decision-making about patient movement. There are some areas where minor improvements could enhance patient safety and your own ergonomics. Continue practicing these skills while incorporating the specific recommendations provided.`;
  }

  // Create the response object
  const response = {
    score: mockScore,
    feedback: mockFeedback,
    results: [
      {
        "Safety Aspect": "Immediate Response & Safety Check",
        "Status": "✅",
        "Observations": "Caregiver quickly approached the patient and performed a good initial assessment. The environment was checked for immediate hazards, and patient consciousness was verified promptly.",
        "Recommendations": "You handled this well. Consider adding a quick verbal check with the patient about what happened just before the fall to gather additional context."
      },
      {
        "Safety Aspect": "Physical Assessment & Injury Evaluation",
        "Status": "⚠️",
        "Observations": "Good evaluation of pain points and mobility. The caregiver checked key areas for injury and assessed the patient's ability to move safely.",
        "Recommendations": "Your assessment was effective. Consider adding a few orientation questions during your evaluation to check cognitive status while you're performing the physical assessment."
      },
      {
        "Safety Aspect": "Decision on Movement & Assistance",
        "Status": "✅",
        "Observations": "Excellent technique in helping the patient to a seated position first before standing. The caregiver used good judgment in determining it was safe to move the patient.",
        "Recommendations": "Your approach here was excellent. Continue using this methodical technique for all patients."
      },
      {
        "Safety Aspect": "Communication & Documentation",
        "Status": "⚠️",
        "Observations": "Good basic communication throughout the process. Documentation included most important elements about the fall.",
        "Recommendations": "Consider using a simple template for fall documentation to ensure all key factors are recorded consistently."
      },
      {
        "Safety Aspect": "Post-Fall Monitoring & Prevention",
        "Status": "⚠️",
        "Observations": "Appropriate arrangements made for follow-up evaluation. The caregiver discussed basic prevention with the patient.",
        "Recommendations": "Consider adding a specific timeframe for follow-up checks (e.g., 2 hours, 24 hours) to monitor for delayed symptoms."
      }
    ],
    positivePoints: [
      {
        category: "Initial approach",
        detail: "You established contact with the patient appropriately and assessed consciousness"
      },
      {
        category: "Communication style",
        detail: "You used clear, simple language appropriate for the patient's condition"
      },
      {
        category: "Condition awareness",
        detail: "You recognized the patient's breathing difficulties and adjusted your approach"
      }
    ],
    importantSteps: [
      {
        category: "Vital signs assessment",
        detail: "Remember to check vital signs before moving a patient who conscious with complaints of dizziness"
      },
      {
        category: "Oxygen saturation check",
        detail: "Consider including this check as part of your assessment for this patient's specific condition"
      },
      {
        category: "Fall cause investigation",
        detail: "Try to investigate what caused the fall while navigating a step in the hallway, which helps with prevention"
      }
    ],
    suggestions: [
      {
        category: "Condition-specific care",
        detail: "Monitor breathing during exertion and offer rest periods for COPD patients"
      },
      {
        category: "Environmental assessment",
        detail: "Take time to look for potential hazards in the environment that might have contributed to the fall"
      },
      {
        category: "Follow-up plan",
        detail: "Try to develop more specific, actionable prevention recommendations"
      },
      {
        category: "Documentation",
        detail: "Include more details about the patient's condition, fall circumstances, and interventions in your reports"
      }
    ]
  };
  
  console.log('Mock response object:', JSON.stringify(response));
  return response;
};
